import React from "react";
import {
  Eye,
  UserCheck,
  Target,
  Mail,
  ShieldCheck,
  Radar,
  CheckCircle2,
} from "lucide-react";
import { KpiMetrics } from "../Types/CandidateIntelligenceTypes";

interface KpiGridProps {
  kpis: KpiMetrics;
  onDrill?: (section: string) => void;
}

const CARD_STYLES: Record<string, string> = {
  views: "from-cyan-500/20 to-slate-900 border-cyan-500/30",
  interest: "from-fuchsia-500/20 to-slate-900 border-fuchsia-500/30",
  matches: "from-emerald-500/20 to-slate-900 border-emerald-500/30",
  invitations: "from-amber-500/20 to-slate-900 border-amber-500/30",
  completion: "from-indigo-500/20 to-slate-900 border-indigo-500/30",
  visibility: "from-sky-500/20 to-slate-900 border-sky-500/30",
};

const defineCards = (k: KpiMetrics) => [
  {
    key: "views",
    icon: Eye,
    label: "Profile views",
    value: k.views,
    caption: "Recruiters viewed your profile in this window",
    style: CARD_STYLES.views,
  },
  {
    key: "interest",
    icon: Mail,
    label: "Invitations received",
    value: k.interest,
    caption: "Recruiter interest in this window",
    style: CARD_STYLES.interest,
  },
  {
    key: "matches",
    icon: Target,
    label: "Matching roles",
    value: k.matches,
    caption: "Active opportunities that fit you",
    style: CARD_STYLES.matches,
  },
  {
    key: "invitations",
    icon: Mail,
    label: "Total invites",
    value: k.invitations,
    caption: "All-time invitations",
    style: CARD_STYLES.invitations,
  },
  {
    key: "completion",
    icon: ShieldCheck,
    label: "Profile strength",
    value: Math.round(k.completion),
    caption: "Profile completion score",
    style: CARD_STYLES.completion,
  },
  {
    key: "visibility",
    icon: Radar,
    label: "Discoveries",
    value: k.visibility,
    caption: "Shown in recruiter searches",
    style: CARD_STYLES.visibility,
  },
];

export const KpiGrid: React.FC<KpiGridProps> = ({ kpis, onDrill }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
    {defineCards(kpis).map((card, i) => {
      const Icon = card.icon;
      return (
        <button
          key={card.key}
          type="button"
          onClick={() => onDrill?.(card.key)}
          className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-br p-4 text-left text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-xl ${card.style}`}
        >
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/10 text-cyan-200 backdrop-blur">
            <Icon size={17} />
          </span>
          <p className="mt-3 text-3xl font-black tabular-nums">{card.value.toLocaleString()}</p>
          <p className="text-xs font-bold uppercase tracking-wide text-white/80">{card.label}</p>
          <p className="mt-1 line-clamp-2 text-[10px] text-white/60">{card.caption}</p>
          <CheckCircle2 size={12} className="absolute right-3 top-3 text-white/30 opacity-0 transition group-hover:opacity-100" />
          <span className="absolute inset-0 ring-1 ring-inset ring-white/5" />
          <span className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-white/5 blur-2xl" />
          {onDrill ? <span className="sr-only">For card {i + 1}</span> : null}
        </button>
      );
    })}
  </div>
);

export const CompletionRing: React.FC<{ percent: number; size?: number }> = ({ percent, size = 64 }) => {
  const stroke = 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, percent)) / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#1e293b" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#kring)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
        />
        <defs>
          <linearGradient id="kring" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
        </defs>
      </svg>
      <span className="absolute inset-0 grid place-items-center text-sm font-black text-cyan-300">
        {Math.round(percent)}%
      </span>
    </div>
  );
};